<template>
	<div v-if="cmsInterests.length" class="card-page interestsSelection-wrapper">
		<ja-header
			:title="$t('gamePlayMetaTitles.interests')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/interests"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="building-wrapper">
				<ja-building-header
					:asset="'page-headers/interests-building'"
					:billboard-asset="localSponsor"
					billboard-position="right"
					type="page-header"
				></ja-building-header>
			</div>

			<h1 class="heading">{{ $t('interestsScreenTitleQuestion') }}</h1>
			<h2 class="sub-heading body-text-1">
				{{ $t('interestsScreenTitleInfo') }}
			</h2>
			<div class="interests-wrapper">
				<ja-card
					v-for="interest in cmsInterests"
					:id="interest.interest_ID"
					:key="interest.interest_ID"
					:name="interest.name"
					:class="{ disabled: interestsCountIsExceeded }"
					:inline="true"
					:selected="interestIsSelected(interest)"
					role="switch"
					:aria-checked="interestIsSelected(interest).toString()"
					@click.native="onInterestClick(interest)"
				>
					{{ interest.name }}
					<ja-multiselect-circle
						aria-hidden="true"
						tabindex="-1"
						:selected="interestIsSelected(interest)"
						@selected="onInterestClick(interest)"
					></ja-multiselect-circle>
				</ja-card>
			</div>
		</content>

		<ja-alert-bar :alert-condition="alertIsVisible">
			{{ $t('interestsCountAlert') }}
		</ja-alert-bar>

		<footer>
			<div class="footer-content">
				<span class="counter">{{
					$t('interestsSelectedCounter', {
						counter: selectedInterestsCount,
						maxCount: REQUIRED_SELECTABLE_INTERESTS
					})
				}}</span>
				<ja-button
					class="small right"
					name="next-interest"
					:disabled="nextButtonIsDisabled()"
					:aria-label="$t('goToClustersPage')"
					@click.native.once="sendInterests"
				>
					<span class="button-text">{{ $t('next') }}</span>
					<svgicon name="arrow-right" class="arrow-right"></svgicon>
				</ja-button>
			</div>
		</footer>
	</div>
</template>
<script>
import '../components/icons/headerIcons/interests';
import gql from 'graphql-tag';
import Button from '../components/ui/Button';
import Header from '../components/ui/headers/Header';
import '../components/icons/arrow-left';
import '../components/icons/arrow-right';
import AlertBar from '../components/ui/AlertBar';
import UserData from '../mixins/UserData';
import Accessibility from '../mixins/Accessibility';
import NavigationLoading from '../mixins/NavigationLoading';
import BuildingHeader from '@/components/ui/headers/BuildingHeader';
import LocalSponsorService from '@/services/LocalSponsorService';
import Card from '@/components/ui/Card';
import MultiselectCircle from '@/components/ui/MultiselectCircle';
import StudentsService from '@/services/StudentsService';
import { profileCreationSteps } from '@/helpers/constants';

export default {
	components: {
		jaHeader: Header,
		jaButton: Button,
		jaCard: Card,
		jaMultiselectCircle: MultiselectCircle,
		jaAlertBar: AlertBar,
		jaBuildingHeader: BuildingHeader
	},
	mixins: [UserData, NavigationLoading, Accessibility],
	data() {
		return {
			cmsInterests: [],
			selectedInterests: [],
			alertIsVisible: false,
			localSponsor: null,
			profileCreationSteps: profileCreationSteps
		};
	},
	computed: {
		selectedInterestsCount() {
			return this.selectedInterests.length;
		},
		interestsCountIsExceeded() {
			return this.selectedInterestsCount >= this.REQUIRED_SELECTABLE_INTERESTS;
		},
		constants() {
			return this.$store.getters.constants;
		},
		REQUIRED_SELECTABLE_INTERESTS() {
			if (this.constants) {
				return this.constants.requiredSelectableInterests;
			}
			return null;
		}
	},
	created() {
		if (this.userData) {
			this.$store.dispatch('getPauseState');
		}
	},
	mounted() {
		LocalSponsorService.getLogo(
			'Interests',
			this.userData.studentFacilityId
		).then(logo => (this.localSponsor = logo));
	},
	apollo: {
		cmsInterests: {
			query: gql`
				{
					cmsInterests(where: "isActive=true") {
						interest_ID
						name
					}
				}
			`,
			update(data) {
				this.$store.commit('setLoading', false);

				return data.cmsInterests;
			}
		}
	},
	methods: {
		nextButtonIsDisabled() {
			return this.selectedInterestsCount < this.REQUIRED_SELECTABLE_INTERESTS;
		},
		interestIsSelected(interest) {
			return this.selectedInterests.some(x => x.name === interest.name);
		},
		onInterestClick(interest) {
			if (this.interestIsSelected(interest)) {
				var selectedInterestIndex = this.selectedInterests.findIndex(
					x => x.name === interest.name
				);
				this.selectedInterests.splice(selectedInterestIndex, 1);

				if (this.alertIsVisible) {
					this.alertIsVisible = false;
				}
			} else {
				if (!this.interestsCountIsExceeded) {
					this.selectedInterests.push(interest);
				} else {
					setTimeout(() => {
						this.announceError(this.$t('interestsCountAlert'));
					}, 400);
					this.alertIsVisible = true;
				}
			}
		},
		async sendInterests() {
			this.$store.commit('setLoading', true);

			const areInterestsCompleted = await StudentsService.isProfileCreationStepCompleted(
				this.profileCreationSteps.INTERESTS,
				this.userData.studentId
			);

			if (areInterestsCompleted) {
				this.$router.push('/clusters').catch(e => e);
				return;
			}

			const list = [];
			for (let key in this.selectedInterests) {
				let item = this.selectedInterests[key];

				// prevent adding an item without interest_ID
				if (item.interest_ID) {
					let interestObj = {};

					interestObj.interest_ID = item.interest_ID;
					interestObj.studentId = this.userData.studentId;

					list.push(interestObj);
				}
			}

			await this.$apollo
				.mutate({
					mutation: gql`
						mutation($list: [StudentInterestAddInputObjectGraphType]) {
							addManyStudentInterest(list: $list) {
								id
								cmsInterest {
									interest_ID
									name
								}
							}
						}
					`,
					variables: {
						list: list
					}
				})
				.then(response => {
					if (response.data) {
						this.$router.push('/clusters').catch(e => e);
					}
				})
				.catch(() => {
					this.$store.commit('setLoading', false);
				});
		}
	}
};
</script>
<style lang="scss" scoped>
.interestsSelection-wrapper {
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;

	content {
		.building-wrapper {
			width: 768px;
			margin: auto;
		}

		.interests-wrapper {
			height: 100%;
			width: 100%;
			max-width: 850px;
			display: -webkit-box;
			display: -ms-flexbox;
			display: flex;
			-webkit-box-orient: horizontal;
			-webkit-box-direction: normal;
			-ms-flex-flow: row wrap;
			flex-flow: row wrap;
			-webkit-box-align: stretch;
			-ms-flex-align: stretch;
			align-items: stretch;
			-webkit-box-pack: center;
			-ms-flex-pack: center;
			justify-content: center;
			margin: 32px 0px;
			margin-left: auto;
			margin-right: auto;

			.card {
				display: flex;
				justify-content: space-between;
				width: 45%;
				margin: 5px;
				@media (min-width: $breakpoint-mid) {
					padding: 32px 24px;
				}
				.checkbox-wrapper {
					float: right;
					display: inline-block;
					line-height: unset;
				}
				&:hover {
					cursor: pointer;
				}
			}
		}
	}
}
footer {
	justify-content: flex-end;
}
</style>
